import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { Result } from "better-result";
import { z } from "zod/v4";
import { apiEnum } from "#/modules/util.helpers/zod-enum";
import { getClassesForOverview, getKlasDetail } from "#/modules/school.klassen/api.klassen.server";
import type { ClassesSearch } from "#/modules/school.klassen/schema.search";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toFieldErrors, translateApiError } from "#/modules/util.errors/translate";
import { toResult } from "#/modules/util.auth/api-errors";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";
import { Leerjaar } from "#/schema.nswag";

const leerjaarEnum = apiEnum(Leerjaar);

export const createKlasSchema = z.object({
  name: z.string().min(1, { error: m["validation.required"]() }),
  leerjaren: z.array(leerjaarEnum).min(1, { error: m["validation.required"]() }),
});

export const updateKlasSchema = z.object({
  klasId: z.uuid(),
  name: z.string().min(1, { error: m["validation.required"]() }),
  leerjaren: z.array(leerjaarEnum).min(1, { error: m["validation.required"]() }),
});

/** Shared client-side form schema (add + edit dialogs). */
export const klasFormSchema = z.object({
  klasId: z.union([z.string().uuid(), z.literal("")]),
  name: z.string().min(1, { error: m["validation.required"]() }),
  leerjaren: z.array(leerjaarEnum).min(1, { error: m["validation.required"]() }),
});

export const getClassesOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: ClassesSearch) => data)
  .handler(async ({ data }) => {
    const result = await getClassesForOverview(data);
    if (Result.isError(result)) throw result.error;
    const response = result.value;

    const totalPages = Math.ceil((response.count ?? 0) / data.pageSize);

    return {
      classes: response.data ?? [],
      totalPages,
      totalCount: response.count ?? 0,
    };
  });

export const getKlasModalData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ klasId: z.string().optional() }))
  .handler(async ({ data }) => {
    if (!data.klasId) {
      return { klas: null };
    }

    const result = await getKlasDetail(data.klasId);

    if (Result.isError(result)) {
      throw result.error;
    }

    return { klas: result.value?.klas ?? null };
  });

export const submitCreateKlas = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(createKlasSchema)
  .handler(async ({ data }) => {
    const body: components["schemas"]["CreateKlasCommand"] = {
      name: data.name,
      leerjaren: data.leerjaren,
    };

    const result = toResult(await client.POST("/school/klassen", { body }));

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

export const submitUpdateKlas = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(updateKlasSchema)
  .handler(async ({ data }) => {
    const body: components["schemas"]["UpdateKlasCommand"] = {
      klasId: data.klasId,
      name: data.name,
      leerjaren: data.leerjaren,
    };

    const result = toResult(await client.PUT("/school/klassen", { body }));

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

export const submitDeleteKlas = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ klasId: z.string() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.DELETE("/school/klassen", {
        body: { klasId: data.klasId },
      }),
    );

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });
